import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Cloud, Server, Loader2, RefreshCw, Camera, Power, KeyRound, Trash2,
  Cpu, MemoryStick, HardDrive, ArrowDownUp, CheckCircle, AlertTriangle, Globe
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MetricChip({ icon: Icon, label, value, unit, warn }) {
  const color = value == null ? "text-zinc-600" : warn && value >= warn ? "text-red-400" : "text-green-400";
  return (
    <div className="flex items-center gap-1.5 bg-black/40 rounded px-2 py-1" data-testid={`metric-${label.toLowerCase()}`}>
      <Icon className="w-3.5 h-3.5 text-zinc-500" />
      <span className="text-[10px] text-zinc-500 uppercase">{label}</span>
      <span className={`text-xs font-mono font-bold ${color}`}>
        {value == null ? "—" : `${value}${unit}`}
      </span>
    </div>
  );
}

function DropletCard({ droplet, onSnapshot, onPowerCycle }) {
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/do/droplets/${droplet.id}/metrics?minutes=60`)
      .then(res => { if (mounted) setMetrics(res.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoadingMetrics(false); });
    return () => { mounted = false; };
  }, [droplet.id]);

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid={`droplet-card-${droplet.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white font-medium flex items-center gap-2">
              <Server className="w-4 h-4 text-blue-400" /> {droplet.name}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {droplet.region} · {droplet.vcpus} vCPU · {Math.round(droplet.memory_mb / 1024)} GB RAM · {droplet.disk_gb} GB disk
            </p>
            {droplet.ip && <p className="text-xs text-zinc-400 font-mono flex items-center gap-1 mt-0.5"><Globe className="w-3 h-3" /> {droplet.ip}</p>}
          </div>
          <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${
            droplet.status === "active"
              ? "text-green-400 bg-green-500/10 border-green-500/20"
              : "text-yellow-400 bg-yellow-500/10 border-yellow-500/20"}`}>
            {droplet.status === "active" ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {droplet.status}
          </span>
        </div>

        {loadingMetrics ? (
          <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading metrics…</div>
        ) : metrics && metrics.agent_available ? (
          <div className="flex flex-wrap gap-2">
            <MetricChip icon={Cpu} label="CPU" value={metrics.cpu_percent} unit="%" warn={85} />
            <MetricChip icon={MemoryStick} label="Mem" value={metrics.memory_percent} unit="%" warn={90} />
            <MetricChip icon={HardDrive} label="Disk" value={metrics.disk_percent} unit="%" warn={85} />
            <MetricChip icon={ArrowDownUp} label="Net" value={metrics.bandwidth_in_mbps != null ? `${metrics.bandwidth_in_mbps}/${metrics.bandwidth_out_mbps}` : null} unit=" Mbps" />
          </div>
        ) : (
          <p className="text-xs text-zinc-600">Metrics unavailable — DO monitoring agent not detected on this droplet.</p>
        )}

        <div className="flex gap-2 pt-1 border-t border-white/5">
          <Button size="sm" variant="outline" className="border-blue-500/20 text-blue-400 hover:bg-blue-500/10 h-7 text-xs"
            onClick={() => onSnapshot(droplet)} data-testid={`snapshot-btn-${droplet.id}`}>
            <Camera className="w-3.5 h-3.5 mr-1" /> Snapshot
          </Button>
          <Button size="sm" variant="outline" className="border-orange-500/20 text-orange-400 hover:bg-orange-500/10 h-7 text-xs"
            onClick={() => onPowerCycle(droplet)} data-testid={`powercycle-btn-${droplet.id}`}>
            <Power className="w-3.5 h-3.5 mr-1" /> Power Cycle
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TokenSetup({ onSaved }) {
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!token.trim()) { toast.error("Paste your DigitalOcean API token"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/do/config`, { token: token.trim() });
      toast.success("Token validated & saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save token"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5 max-w-xl">
      <CardContent className="p-5 space-y-3">
        <p className="text-white font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-yellow-400" /> Connect DigitalOcean</p>
        <p className="text-xs text-zinc-400">
          Create a token at <span className="text-blue-400">cloud.digitalocean.com → API → Tokens</span> with scopes:
          droplet read+update, image/snapshot read, monitoring read. <span className="text-red-400">Do NOT grant delete scopes.</span>
          Stored encrypted, never exposed to the browser.
        </p>
        <div className="flex gap-2">
          <Input type="password" value={token} onChange={e => setToken(e.target.value)} placeholder="dop_v1_..."
            className="bg-black/50 border-white/10 text-white font-mono" data-testid="do-token-input" />
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="do-token-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate & Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CloudDO() {
  const [config, setConfig] = useState(null);
  const [droplets, setDroplets] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapDialog, setSnapDialog] = useState(null);
  const [snapName, setSnapName] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = (await axios.get(`${API}/do/config`)).data;
      setConfig(cfg);
      if (cfg.configured) {
        const [dRes, sRes] = await Promise.all([
          axios.get(`${API}/do/droplets`),
          axios.get(`${API}/do/snapshots`),
        ]);
        setDroplets(dRes.data);
        setSnapshots(sRes.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load DigitalOcean data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doSnapshot = async () => {
    setSnapBusy(true);
    try {
      const res = await axios.post(`${API}/do/droplets/${snapDialog.id}/snapshot`, { name: snapName || `serverai-${snapDialog.name}` });
      toast.success(`Snapshot started (action ${res.data.action_id}) — takes a few minutes`);
      setSnapDialog(null); setSnapName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Snapshot failed"); }
    finally { setSnapBusy(false); }
  };

  const doPowerCycle = async (droplet) => {
    if (!window.confirm(`⚠️ POWER CYCLE ${droplet.name}?\n\nThis is a HARD reboot (like pressing the reset button). Only do this if the server is frozen and SSH is unreachable.`)) return;
    try {
      const res = await axios.post(`${API}/do/droplets/${droplet.id}/power-cycle`, { confirm: true });
      toast.success(`Power cycle initiated (action ${res.data.action_id})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Power cycle failed"); }
  };

  const removeToken = async () => {
    if (!window.confirm("Remove the DigitalOcean token?")) return;
    await axios.delete(`${API}/do/config`);
    toast.success("Token removed");
    fetchAll();
  };

  return (
    <div className="p-6 space-y-6" data-testid="cloud-do-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Cloud className="w-8 h-8 text-blue-400" /> Cloud — DigitalOcean
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Droplet health, snapshots before risky changes, and break-glass power actions</p>
        </div>
        {config?.configured && (
          <div className="flex gap-2 items-center">
            <span className="text-xs text-zinc-500 font-mono">{config.token_preview}</span>
            <Button size="sm" variant="outline" className="border-white/10 h-8" onClick={removeToken} data-testid="do-token-remove-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="bg-white/5 border-white/10 text-white h-8" onClick={fetchAll}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>
      ) : !config?.configured ? (
        <TokenSetup onSaved={fetchAll} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {droplets.map(d => (
              <DropletCard key={d.id} droplet={d}
                onSnapshot={(dr) => { setSnapDialog(dr); setSnapName(`serverai-${dr.name}-${new Date().toISOString().slice(0, 10)}`); }}
                onPowerCycle={doPowerCycle} />
            ))}
            {droplets.length === 0 && <p className="text-zinc-500 text-sm">No droplets found on this account.</p>}
          </div>

          <div className="space-y-2">
            <p className="text-sm text-zinc-400 flex items-center gap-2"><Camera className="w-4 h-4" /> Snapshots ({snapshots.length})</p>
            {snapshots.length === 0 ? (
              <p className="text-xs text-zinc-600">No droplet snapshots yet. Take one before any risky change.</p>
            ) : (
              <div className="space-y-1">
                {snapshots.map(s => (
                  <div key={s.id} className="flex items-center gap-3 bg-[#0c0c0e] border border-white/5 rounded px-3 py-2 text-xs">
                    <Camera className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    <span className="text-white flex-1 truncate">{s.name}</span>
                    <span className="text-zinc-500">{s.size_gb} GB</span>
                    <span className="text-zinc-600">{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {snapDialog && (
        <Dialog open onOpenChange={() => setSnapDialog(null)}>
          <DialogContent aria-describedby={undefined} className="bg-[#0c0c0e] border-white/10 text-white max-w-md">
            <DialogHeader><DialogTitle className="font-mono">Snapshot {snapDialog.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-zinc-400">The droplet keeps running. Snapshot takes a few minutes and appears in the list below when done.</p>
              <Input value={snapName} onChange={e => setSnapName(e.target.value)}
                className="bg-black/50 border-white/10 text-white" data-testid="snapshot-name-input" />
              <div className="flex gap-2">
                <Button onClick={doSnapshot} disabled={snapBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="snapshot-confirm-btn">
                  {snapBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" /> Create Snapshot</>}
                </Button>
                <Button variant="outline" className="border-white/10" onClick={() => setSnapDialog(null)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
